import { generateText } from '@tiptap/core';
import { EDITABLE_WIDGET_CLASS } from 'constants/bounding-box';
import { oldIconRegex } from 'constants/icon';
import { getRotatedRectByAngle } from 'hooks/useSmartGuide/useSmartGuide.helpers';
import { SnapRect } from 'hooks/useSmartGuide/useSmartGuide.types';
import { customAlphabet } from 'nanoid';
import { PageId, WidgetId } from 'types/idTypes';
import { InfographState, PageSize, WidgetsMap } from 'types/infographTypes';
import { Page } from 'types/pageTypes';
import { TreeNode } from 'types/structuredContentTypes';
import { AccessibleElement, Widget, WidgetType } from 'types/widget.types';
import { rotatePointAroundOrigin } from 'utils/transform';

import { BasicShapeWidgetData } from './BasicShapeWidget/BasicShapeWidget.types';
import { GroupWidgetData } from './GroupWidget/GroupWidget.types';
import { IconWidgetData } from './IconWidget/IconWidget.types';
import { LineWidgetData, Rect } from './Lines/LineWidget/LineWidget.types';
import { parseFloatToLinePrecision } from './Lines/LineWidget/StepsLineWidget/StepsLineWidget.helpers';
import { extensions } from './TextBasedWidgets/TextWidget/TextWidgetExtensions';
import { WIDGET_LABEL_MAP } from './Widget.config';
import { AllWidgetData } from './Widget.types';

/**
 * List of widget types that use the 'fit-content' as css height.
 * It's for changing the widget base height dynamically based on the contents.
 * TODO: It should be in the Widget.config.ts but it has an error.
 */
const AUTO_HEIGHT_WIDGET_TYPES: WidgetType[] = [
  WidgetType.Text,
  WidgetType.Table,
  WidgetType.StatChart,
  WidgetType.LabelText,
];

/**
 * List of widget types that use 'fit-content' as css width.
 * It changes the widget base width dynamically based on the contents.
 */
const AUTO_WIDTH_WIDGET_TYPES: WidgetType[] = [WidgetType.Table];

/**
 *
 * Used to generate random id for widget. 24 chars.
 * (note the actual generate functions prefixes with widget type, which is 3 chars, plus 1 period separator)
 * So the final widget id should be 28 chars
 */
const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', 24);

/**
 * Generates unique ID for any given type of the widget.
 *
 * @param widgetType Type of widget to generate ID for
 * @returns Unique id of the widget, with type information encoded
 */
export function generateWidgetId(widgetType: WidgetType): WidgetId {
  return `${widgetType}.${nanoid()}`;
}

/**
 * Returns the widgetType of a widgetId string
 *
 * @param widgetId ID of the widget, to determine type of the widget
 * @returns Returns the widgetType of a widgetId string
 */
export function getWidgetTypeFromId(widgetId: WidgetId): WidgetType {
  const widgetType: WidgetType = widgetId.substring(0, 3) as WidgetType;
  return widgetType;
}

/**
 * Generates the label to be announced by screen readers
 * @param widgetId ID of the widget
 * @param data the widget's data
 * @returns label
 */
export function getWidgetLabelInformation(
  widgetId: WidgetId,
  // TODO: refactor this its too coupled with the text widget
  proseMirrorData: any,
  textTag: string,
  altText: string,
): {
  widgetLabel: string;
  widgetDescription: string;
} {
  const widgetType = getWidgetTypeFromId(widgetId);
  let widgetLabel, widgetDescription;

  switch (widgetType) {
    case WidgetType.Text:
      try {
        const textContent = generateText(proseMirrorData, extensions);

        widgetLabel = `${textTag} text widget`;
        widgetDescription = textContent;
      } catch (error) {
        console.error(error);
        widgetLabel = '';
        widgetDescription = '';
      }

      break;
    case WidgetType.Icon:
    case WidgetType.BasicShape:
    case WidgetType.Line:
    case WidgetType.Image:
    case WidgetType.AreaChart:
    case WidgetType.BarChart:
    case WidgetType.ColumnChart:
    case WidgetType.HalfPieChart:
    case WidgetType.HistogramChart:
    case WidgetType.LineChart:
    case WidgetType.PieChart:
    case WidgetType.StackedAreaChart:
    case WidgetType.StackedBarChart:
    case WidgetType.StackedColumnChart:
    case WidgetType.WordCloudChart:
      widgetLabel = WIDGET_LABEL_MAP[widgetType] ?? '';
      widgetDescription = altText || '';
      break;
    default:
      widgetLabel = '';
      widgetDescription = '';
  }

  return {
    widgetLabel,
    widgetDescription,
  };
}

/**
 * Return all widget ids in the page
 *
 * WARN: This function is used in the backend and frontend.
 *       Any changes should be applied to both sides.
 *
 * @param page
 * @param widgets
 * @param includeAllGroupWidgets
 * @returns
 * @see editor2-api/src/types/utils.ts
 */
export const getPageWidgetIds = (
  page: Page,
  widgets: WidgetsMap,
  includeAllGroupWidgets: boolean = true,
): WidgetId[] => {
  const widgetIds: WidgetId[] = [];
  if (!page || !page.widgetLayerOrder) return widgetIds;

  page.widgetLayerOrder.forEach((widgetId: WidgetId) => {
    const isGroup = widgets[widgetId].hasOwnProperty('memberWidgetIds');

    if (includeAllGroupWidgets && isGroup) {
      const groupWidget = widgets[widgetId] as GroupWidgetData;
      groupWidget.memberWidgetIds.forEach((memberWidgetId: WidgetId) => {
        widgetIds.push(memberWidgetId);
        if (widgets[memberWidgetId].hasOwnProperty('memberWidgetIds')) {
          const nestedMemberWidgetIds = (widgets[memberWidgetId] as GroupWidgetData).memberWidgetIds;
          nestedMemberWidgetIds.forEach((id: WidgetId) => widgetIds.push(id));
        }
      });
    }

    widgetIds.push(widgetId);
  });

  return widgetIds;
};

/**
 * Gets all the widgets that need to be rendered in page.
 * For GROUP widgets, only includes the member widgets.
 *
 * If shouldFlattenStatcharts is true, it will include only the member widgets of the stat chart group but not the stat chart group itself.
 *
 * Else for RESPONSIVE GROUP widgets, only includes the container widget
 *  - It does not include the member widgets since they will be rendered
 *    by the parent widget.
 *
 * @param page
 * @param widgets
 * @returns WidgetId[] widget ids that need to be rendered
 */
export const getFlattenedLayerOrder = (
  page: Page,
  widgets: WidgetsMap,
  shouldFlattenStatcharts: boolean = false,
): WidgetId[] => {
  const widgetIds: WidgetId[] = [];
  if (!page || !page.widgetLayerOrder) return widgetIds;

  page.widgetLayerOrder.forEach((widgetId: WidgetId) => {
    const isGroup = getWidgetTypeFromId(widgetId) === WidgetType.Group;
    const isStatchartGroup = getWidgetTypeFromId(widgetId) === WidgetType.StatChart && shouldFlattenStatcharts;

    if (isGroup) {
      const groupWidget = widgets[widgetId] as GroupWidgetData;
      groupWidget.memberWidgetIds.forEach((memberWidgetId: WidgetId) => widgetIds.push(memberWidgetId));
    }

    if (isStatchartGroup) {
      const statChartWidget = widgets[widgetId] as GroupWidgetData;
      statChartWidget.memberWidgetIds.forEach((memberWidgetId: WidgetId) => {
        widgetIds.push(memberWidgetId);
      });
    }

    if (!isGroup && !isStatchartGroup) widgetIds.push(widgetId);
  });

  return widgetIds;
};

/**
 * Return the true if the widget position overlap
 *
 * @param widgetA
 * @param widgetB
 * @param tolerance - optional tolerance value for overlap - for near overlap
 * @returns {boolean}
 */
export const intersectRect = (widgetA: SnapRect, widgetB: SnapRect, tolerance?: number): boolean => {
  // Check overlap considering a px tolerance (optional)
  if (typeof tolerance === 'number') {
    // Round values to the nearest whole number
    const roundA = {
      leftPx: Math.round(widgetA.leftPx),
      topPx: Math.round(widgetA.topPx),
      widthPx: Math.round(widgetA.widthPx),
      heightPx: Math.round(widgetA.heightPx),
    };
    const roundB = {
      leftPx: Math.round(widgetB.leftPx),
      topPx: Math.round(widgetB.topPx),
      widthPx: Math.round(widgetB.widthPx),
      heightPx: Math.round(widgetB.heightPx),
    };

    return !(
      roundB.leftPx > roundA.leftPx + roundA.widthPx + tolerance ||
      roundB.leftPx + roundB.widthPx < roundA.leftPx - tolerance ||
      roundB.topPx > roundA.topPx + roundA.heightPx + tolerance ||
      roundB.topPx + roundB.heightPx < roundA.topPx - tolerance
    );
  }

  return !(
    widgetB.leftPx > widgetA.leftPx + widgetA.widthPx ||
    widgetB.leftPx + widgetB.widthPx < widgetA.leftPx ||
    widgetB.topPx > widgetA.topPx + widgetA.heightPx ||
    widgetB.topPx + widgetB.heightPx < widgetA.topPx
  );
};

/**
 * Check if widget A is wrapping around (contains) widget B
 * @param a - SnapRect A
 * @param b - SnapRect B
 */
export const isWidgetWrapping = (a: SnapRect, b: SnapRect) => {
  const topPxGreater = Math.round(a.topPx) <= Math.round(b.topPx);
  const widthGreater = Math.round(a.widthPx) >= Math.round(b.widthPx);
  const heightGreater = Math.round(a.heightPx) >= Math.round(b.heightPx);
  const bottomPxGreater = Math.round(a.topPx) + Math.round(a.heightPx) >= Math.round(b.topPx) + Math.round(b.heightPx);
  const rightPxGreater =
    Math.round(a.leftPx) <= Math.round(b.leftPx) &&
    Math.round(a.leftPx) + Math.round(a.widthPx) >= Math.round(b.leftPx) + Math.round(b.widthPx);

  const isWrapping = topPxGreater && widthGreater && heightGreater && bottomPxGreater && rightPxGreater;
  return isWrapping;
};

/**
 * Note: This is a temporary solution, so it should be resolved using the Java library VenngagePDFUtil later.
 *
 * Headlesschrome generates PDF file's reading order using zIndex.
 * So that, Adobe Acrobat reader and NVDA use the reading order based on the element's zIndex.
 * Re-calculate the zIndex-ordered array using the structure tree and the array of layers.
 *
 * If Reading order widget is covered by another widget, it is an unusual use case.
 * These widgets are excluded from the calculation.
 *
 * @param widgets
 * @param widgetOrderList
 * @param layers
 * @returns {WidgetId[]}
 */
export const calculateZIndexOrdering = (
  widgets: WidgetsMap,
  widgetOrderList: WidgetId[],
  layers: WidgetId[],
): WidgetId[] => {
  let readingOrderIdList = widgetOrderList
    /* Get widgets used in reading order */
    .filter((widgetId: string) => (widgets[widgetId] as AccessibleElement)?.isDecorative !== true)
    /* Filter the overlapped widget */
    .filter((widgetId: string) => {
      const targetWidgetData = widgets[widgetId] as AllWidgetData;

      // Ignore invalid widgetIDs in the structureTree
      if (!targetWidgetData) {
        console.warn(`Not found this widgetId(structureTree) in the widget data list: ${widgetId}`);
        return false;
      }

      const targetWidgetLayerIndex = layers.findIndex((wId) => wId === widgetId);

      // Ignore if this widgetId doesn't exist in layers
      if (targetWidgetLayerIndex === -1) {
        console.warn(`Not found this widgetId(structureTree) in layers: ${widgetId}`);
        return false;
      }

      const rotatedTargetWidgetRect = getRotatedRectByAngle(targetWidgetData, targetWidgetData.rotateDeg);

      // If it overlaps position with the widget of the upper layer, it will be removed
      for (let i = targetWidgetLayerIndex + 1; i < layers.length; i += 1) {
        const isOverlapped = intersectRect(
          rotatedTargetWidgetRect,
          getRotatedRectByAngle(widgets[layers[i]], widgets[layers[i]].rotateDeg),
        );

        if (isOverlapped) return false;
      }

      return true;
    });

  // Remove the widget ids included in the Reading order from layers and push it at the end of the array.
  return layers.filter((widgetId) => !readingOrderIdList.includes(widgetId)).concat(readingOrderIdList);
};

/**
 * Check if this widget type uses fit-content as the height for dynamic height adjustment using content.
 * @param widgetId
 * @returns {boolean}
 */
export const isAutoHeightWidget = (widgetId: WidgetId | WidgetType): boolean =>
  AUTO_HEIGHT_WIDGET_TYPES.includes(getWidgetTypeFromId(widgetId));

/**
 * Check if this widget type uses fit-content as the height for dynamic height adjustment using content.
 * @param widgetId
 * @returns {boolean}
 */
export const isAutoWidthWidget = (widgetId: WidgetId | WidgetType): boolean =>
  AUTO_WIDTH_WIDGET_TYPES.includes(getWidgetTypeFromId(widgetId));

/**
 * Blur on active widget
 * @param widgetId
 */
export const blurActiveWidget = (widgetId: string): void => {
  const activeWidget = document.getElementById(widgetId);
  if (activeWidget) (activeWidget as HTMLElement).blur();
};

/**
 * get all leaf node of structure tree
 *
 * NOTE: This function is used in the backend and frontend.
 *       Any changes should be applied to both sides.
 *
 * @param t
 * @returns {string[]}
 * @see editor2-api/src/types/utils.ts
 */
export function getAllLeafOfTree(t: TreeNode): string[] {
  const [, , child] = t;
  if (typeof child === 'string') {
    return [child];
  } else {
    return child.map((c) => (typeof c === 'string' ? [c] : getAllLeafOfTree(c))).flat();
  }
}

/**
 * Check if the widget is within the page bounds
 * A widget that is partially outside the page is considered to be within the page
 *
 * @param widgets widgets map
 * @param widgetId widget id of the widget to check
 * @param size page size
 *
 * @returns {boolean}
 */
export const isWidgetWithinPage = (widgets: WidgetsMap, widgetId: WidgetId, size: PageSize) => {
  const widget = widgets[widgetId];
  if (!widget) return false;

  const type = getWidgetTypeFromId(widgetId);

  // for group widgets check that all member widgets are outside the page
  if (type === WidgetType.Group) {
    const isOutsidePageWidgetList = [];
    const memberCount = (widget as GroupWidgetData).memberWidgetIds.length;

    for (let i = 0; i < memberCount; i++) {
      const memberWidgetId = (widget as GroupWidgetData).memberWidgetIds[i];

      const isMemberWithinPage = isWidgetWithinPage(widgets, memberWidgetId, size);
      if (!isMemberWithinPage) isOutsidePageWidgetList.push(memberWidgetId);
    }

    // check if all group member widgets are outside the page
    if (isOutsidePageWidgetList.length === memberCount) return false;
    return true;
  }

  const { topPx, leftPx, widthPx, heightPx } = widget;
  const rightPx = leftPx + widthPx;
  const bottomPx = topPx + heightPx;

  // is outside on the left
  if (rightPx <= 0) return false;
  // is outside on the right
  if (leftPx >= size.widthPx) return false;
  // is outside on the top
  if (bottomPx <= 0) return false;
  // is outside on the bottom
  if (topPx >= size.heightPx) return false;

  return true;
};

/**
 * Takes a transform matrix of an element and returns the resulting Rect.
 *
 * @param target - The HTML element to calculate the new rectangle for.
 * @param matrix - The DOMMatrix representing the transform matrix applied to the element.
 *
 * @returns {Rect} - The new rectangle with the following properties:
 *   - x: The new x-coordinate after applying the transform matrix.
 *   - y: The new y-coordinate after applying the transform matrix.
 *   - width: The new width after applying the transform matrix.
 *   - height: The new height after applying the transform matrix.
 */
export const calculateNewRect = (target: HTMLElement, matrix: DOMMatrix): Rect => {
  const { e: translateX, f: translateY } = matrix;
  const width = target.offsetWidth;
  const height = target.offsetHeight;
  const x = target.offsetLeft + translateX;
  const y = target.offsetTop + translateY;

  const rotationAngle = Math.atan2(matrix.b, matrix.a); // this is correct
  const rotationAngleInDegrees = rotationAngle * (180 / Math.PI); // this is correct

  const boundingRect = getBoundingRectOfRotatedWidget(x, y, width, height, rotationAngleInDegrees);

  return {
    x: boundingRect.leftPx,
    y: boundingRect.topPx,
    width: boundingRect.widthPx,
    height: boundingRect.heightPx,
  };
};

/**
 * Generates the bounding rectangle of a given widget, taking into account its rotation.
 *
 * @param {Widget | null} widget - The widget object
 * @returns {Rect | null} An object representing the bounding rectangle of the widget, or null if the widget is null.
 *         The returned Rect object has properties: x, y, width, height.
 */
export const generateBoundingRectFromWidget = (widget: Widget | null): Rect | null => {
  if (!widget) {
    return null;
  }

  const { topPx, leftPx, widthPx, heightPx, rotateDeg } = widget;

  // Get the bounding rectangle of the rotated widget
  const {
    leftPx: x,
    topPx: y,
    widthPx: width,
    heightPx: height,
  } = getBoundingRectOfRotatedWidget(leftPx, topPx, widthPx, heightPx, rotateDeg);

  // Return the bounding rectangle
  return {
    x,
    y,
    width,
    height,
  };
};

/**
 * Calculates the bounding rectangle of a rotated widget.
 *
 * @param {number} x - The x-coordinate of the top-left corner of the original rectangle.
 * @param {number} y - The y-coordinate of the top-left corner of the original rectangle.
 * @param {number} width - The width of the original rectangle.
 * @param {number} height - The height of the original rectangle.
 * @param {number} rotateDeg - The rotation angle in degrees.
 * @returns An object representing the bounding rectangle of the rotated widget, with properties:
 *          leftPx (x-coordinate of the top-left corner),
 *          topPx (y-coordinate of the top-left corner),
 *          widthPx (width of the bounding rectangle),
 *          heightPx (height of the bounding rectangle).
 */
export const getBoundingRectOfRotatedWidget = (
  x: number,
  y: number,
  width: number,
  height: number,
  rotateDeg: number,
) => {
  // Define the center of the rectangle
  const center = { xPx: x + width / 2, yPx: y + height / 2 };

  // Coordinates of the rectangle's corners before rotation
  const corners = [
    { xPx: x, yPx: y },
    { xPx: x + width, yPx: y },
    { xPx: x + width, yPx: y + height },
    { xPx: x, yPx: y + height },
  ];

  // Rotate each corner around the center
  const rotatedCorners = corners.map((corner) => rotatePointAroundOrigin(corner, center, rotateDeg));
  // Calculate the bounding rectangle
  const minX = Math.min(...rotatedCorners.map((corner) => corner.xPx));
  const minY = Math.min(...rotatedCorners.map((corner) => corner.yPx));
  const maxX = Math.max(...rotatedCorners.map((corner) => corner.xPx));
  const maxY = Math.max(...rotatedCorners.map((corner) => corner.yPx));

  return {
    leftPx: parseFloatToLinePrecision(minX),
    topPx: parseFloatToLinePrecision(minY),
    widthPx: parseFloatToLinePrecision(maxX - minX),
    heightPx: parseFloatToLinePrecision(maxY - minY),
  };
};

/**
 * Check if a page is using flow mode by checking for line widgets that have a start or end port
 *
 * @param infographState
 * @param pageId - page to check for flow mode
 * @returns boolean TRUE if the design has flow mode, FALSE otherwise
 */
export const checkPageHasFlowMode = (infographState: InfographState, pageId: PageId): boolean => {
  const widgets = infographState.widgets;
  const widgetIds = getPageWidgetIds(infographState.pages[pageId], widgets);

  return widgetIds.some((widgetId) => {
    if (getWidgetTypeFromId(widgetId) !== WidgetType.Line) return false;

    const lineWidgetData = widgets[widgetId] as LineWidgetData;
    return !!lineWidgetData?.startPort?.widgetId || !!lineWidgetData?.endPort?.widgetId;
  });
};

/**
 * Get all widget ids that are used in the pages
 *
 *
 * @param infograph
 * @returns
 * @see frontend/src/widgets/Widget.helpers.ts
 */
export const getAllPageWidgetIds = (infograph: InfographState): WidgetId[] => {
  const { widgets, pages } = infograph;
  let allPageWidgetIds: WidgetId[] = [];

  Object.keys(pages).forEach((pageId) => {
    const page = pages[pageId];
    const pageWidgetIds = getPageWidgetIds(page, widgets);

    allPageWidgetIds = [...allPageWidgetIds, ...pageWidgetIds];

    // Check the widgetId in the structure tree
    if (page?.widgetStructureTree && page.widgetStructureTree.length) {
      allPageWidgetIds = [...allPageWidgetIds, ...getAllLeafOfTree(page.widgetStructureTree)];
    }
  });

  return allPageWidgetIds;
};

/**
 *  Check if a icon widget is an old unsupported icon from E1
 *  Old icons are not supported in the new editor and cause export issues
 *
 * @param widgets
 * @param widgetId
 * @returns
 */
export const isOldIcon = (widgets: WidgetsMap, widgetId: WidgetId) => {
  if (!widgetId || !widgets) return true;

  const type = getWidgetTypeFromId(widgetId);
  if (type !== WidgetType.Icon) return false;

  const iconWidgetData = widgets[widgetId] as IconWidgetData;
  if (!iconWidgetData) return false;

  const iconId = iconWidgetData?.iconId;
  return oldIconRegex.test(iconId);
};

/**
 * Check if a  widget has a invalid fillColor, i.e. empty fill color.
 * Invalid  fillColor can cause editor issues.
 *
 * @param widgets
 * @param widgetId
 * @returns
 */
export const hasInvalidFillColor = (widgets: WidgetsMap, widgetId: WidgetId) => {
  if (!widgetId || !widgets) return true;

  const type = getWidgetTypeFromId(widgetId);
  if (type !== WidgetType.BasicShape) return false;

  const widgetData = widgets[widgetId] as BasicShapeWidgetData;
  if (!widgetData) return true;

  if (!widgetData.fillColor) return true;

  return false;
};

/**
 * Check if a  widget has a invalid size, i.e. height or width is 0 or less.
 * Invalid  sizes can cause export issues.
 *
 * @param widgets
 * @param widgetId
 * @returns
 */
export const hasInvalidSize = (widgets: WidgetsMap, widgetId: WidgetId) => {
  if (!widgetId || !widgets) return true;
  const widgetData = widgets[widgetId];
  if (!widgetData) return true;

  // OBS: Only for images for now - can be expanded to other widgets later
  const type = getWidgetTypeFromId(widgetId);
  if (type !== WidgetType.Image) return false;

  const heightPx = widgetData?.heightPx;
  const widthPx = widgetData?.widthPx;

  // if either height or width is null the size is considered invalid
  if (heightPx === null || widthPx === null) return true;

  // only check numbers for now - can add more checks later if needed
  if (typeof heightPx !== 'number' || typeof widthPx !== 'number') return false;

  return heightPx <= 0 || widthPx <= 0;
};

/**
 * Retrieves the IDs of rendered (in the dom) editable widgets
 *
 * @param widgetType - Optional widget type to filter the IDs by.
 * @returns An array of widget IDs.
 */
export const getRenderedEditableWidgetIds = (widgetType?: WidgetType): WidgetId[] => {
  const widgetEls = document.querySelectorAll(`.${EDITABLE_WIDGET_CLASS}`);
  if (!widgetEls || !widgetEls.length) return [];

  const widgetIds: WidgetId[] = [];
  for (let i = 0; i < widgetEls.length; i++) {
    const widgetId = widgetEls[i]?.id;
    // if the widget type is provided only include widget type ids - else return all ids
    if (widgetType && getWidgetTypeFromId(widgetId) !== widgetType) continue;
    widgetIds.push(widgetId);
  }

  return widgetIds;
};

/**
 * Check if line widget has connected ports
 * If line widget has no start or end ports, it will return false
 * If line widget has either start or end ports, it will return true
 *
 * @param lineWidgetData
 * @returns boolean
 */
export const checkIfLineWidgetHasConnectedPort = (lineWidgetData: LineWidgetData): boolean => {
  const hasConnectedEnd =
    (!!lineWidgetData && 'endPort' in lineWidgetData && !!lineWidgetData.endPort?.widgetId) ||
    (!!lineWidgetData && 'startPort' in lineWidgetData && !!lineWidgetData?.startPort?.widgetId);
  return hasConnectedEnd;
};

/**
 * Helper to handle mirror images, icons...
 *
 * @param mirror
 * @returns style
 */
export const generateShapeStyle = (mirror: { isVertical?: boolean; isHorizontal?: boolean }) => {
  const style = { position: 'absolute' as 'absolute' };

  const { isHorizontal, isVertical } = mirror || {};
  const scaleX = isHorizontal ? -1 : 1;
  const scaleY = isVertical ? -1 : 1;

  if (isVertical || isHorizontal) {
    return {
      ...style,
      transform: `scale(${scaleX}, ${scaleY})`,
      transformOrigin: 'center',
    };
  }
  return style;
};

/**
 * Finds the closest widget below the selected widget within a given threshold.
 *
 * @param selectedWidget - The selected widget.
 * @param pageWidgetMap - The map of widgets on the page.
 * @param threshold - The threshold value for vertical distance (default: 100).
 *
 * @returns The closest widget below the selected widget, or null if no widget is found.
 */
export const findClosestWidgetBelow = (
  selectedWidget: Widget & { widgetId: string },
  pageWidgetMap: WidgetsMap,
  threshold: number = 100,
): (Widget & { widgetId: string }) | null => {
  const { topPx, leftPx, widthPx, heightPx, widgetId } = selectedWidget;
  const bottomPx = topPx + heightPx; // Bottom edge of the selectedWidget

  let closestWidget = null;
  let closestDistance = Infinity;

  Object.entries(pageWidgetMap).forEach(([pageWidgetId, widget]) => {
    const isBelow = widget.topPx > bottomPx;
    const isWithinOrBelowThreshold = widget.topPx - bottomPx < threshold;

    if (isBelow && isWithinOrBelowThreshold && pageWidgetId !== widgetId) {
      // Ensure the box is vertically below
      // Calculate the horizontal span of both boxes to check for overlap
      const selectedBoxRightEdge = leftPx + widthPx;
      const boxRightEdge = widget.leftPx + widget.widthPx;

      // Check if there's any overlap in the horizontal plane
      const isOverlappingHorizontally =
        widget.leftPx <= selectedBoxRightEdge && // Left edge of the box is to the left of the right edge of the selectedWidget
        boxRightEdge >= leftPx; // Right edge of the box is to the right of the left edge of the selectedWidget

      if (isOverlappingHorizontally) {
        const verticalDistance = widget.topPx - bottomPx; // Vertical distance from selectedWidget to this box

        // Update closest box if this one is closer
        if (verticalDistance < closestDistance) {
          closestDistance = verticalDistance;
          closestWidget = { widgetId: pageWidgetId, ...widget };
        }
      }
    }
  });

  return closestWidget;
};
